import DateFnsAdapter from '@date-io/date-fns';
// eslint-disable-next-line no-unused-vars
import { DateRange } from './types';
import max from './max';
import min from './min';

const dateFns = new DateFnsAdapter();

const {
    startOfWeek,
    startOfMonth,
    endOfWeek,
    endOfMonth,
    isBefore,
    addDays,
    isSameDay,
    isWithinRange,
    isSameMonth,
    addMonths,
    parse,
    parseISO,
    isValid,
} = dateFns;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const identity = <T>(x: T) => x;

export const chunks = <T>(array: ReadonlyArray<T>, size: number): T[][] =>
    Array.from({ length: Math.ceil(array.length / size) }, (_v, i) =>
        array.slice(i * size, i * size + size)
    );

export const combine = (...args: any[]): string =>
    args.filter(identity).join(' ');

// Date
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const getDaysInMonth = (date: Date) => {
    const startWeek = startOfWeek(startOfMonth(date));
    const endWeek = endOfWeek(endOfMonth(date));
    const days = [];
    for (let curr = startWeek; isBefore(curr, endWeek); ) {
        days.push(curr);
        curr = addDays(curr, 1);
    }
    return days;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const isStartOfRange = ({ startDate }: DateRange, day: Date) =>
    (startDate && isSameDay(day, startDate)) as boolean;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const isEndOfRange = ({ endDate }: DateRange, day: Date) =>
    (endDate && isSameDay(day, endDate)) as boolean;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const inDateRange = ({ startDate, endDate }: DateRange, day: Date) =>
    (startDate &&
        endDate &&
        (isWithinRange(day, [startDate, endDate]) ||
            isSameDay(day, startDate) ||
            isSameDay(day, endDate))) as boolean;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const isRangeSameDay = ({ startDate, endDate }: DateRange) => {
    if (startDate && endDate) {
        return isSameDay(startDate, endDate);
    }
    return false;
};

type Falsy = false | null | undefined | 0 | '';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const parseOptionalDate = (
    date: Date | string | Falsy,
    defaultValue: Date
) => {
    if (typeof date === 'string') {
        const parsed = parse(date, 'ff');
        if (isValid(parsed)) return parsed;
    }
    if (typeof date === 'object' && date) {
        const parsed = parseISO(new Date(date).toISOString());
        if (isValid(parsed)) return parsed;
    }
    return defaultValue;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const getValidatedMonths = (
    range: DateRange,
    minDate: Date,
    maxDate: Date
) => {
    const { startDate, endDate } = range;
    if (startDate && endDate) {
        const newStart = max([startDate, minDate]);
        const newEnd = min([endDate, maxDate]);

        return [
            newStart,
            isSameMonth(newStart, newEnd) ? addMonths(newStart, 1) : newEnd,
        ];
    }
    return [startDate, endDate];
};
